"use client";

import { useRef, useState, useMemo, useEffect } from "react";
import PrimaryBtn from "@/components/PrimaryBtn";
import Image from "next/image";
import { Images, Icons } from "@/app/assets/shared";
import Link from "next/link";
import { WhitepaperInterface } from "@/lib/Types";
import WhitepaperDownloadPopup from "@/app/resources/WhitepaperDownloadPopup";
import { useIsMobile } from "@/hooks/useMediaQuery";
import DOMPurify from "isomorphic-dompurify";

type BlogPost = {
  id?: number | string;
  title?: string;
  excerpt?: string;
  category?: {
    id: number;
    name: string;
    slug: string;
  };
  image?: string;
  slug?: string;
};

const BlogSkeletonCard = () => (
  <article className="p-[42px] bg-white/10 flex flex-col justify-between insight-card animate-pulse h-full min-h-[500px]">
    <div className="flex flex-col gap-[30px]">
      <div className="h-4 w-1/3 bg-white/20 rounded" />
      <div className="h-6 w-full bg-white/20 rounded" />
      <div className="h-6 w-5/6 bg-white/20 rounded" />
      <div className="h-4 w-full bg-white/10 rounded" />
    </div>
    <div className="mt-8 h-12 w-12 rounded-full bg-white/20" />
  </article>
);

type Props = {
  relatedBlogPost: BlogPost[];
  whitePaper: WhitepaperInterface | null;
  value: "tag" | "category";
};

export default function InsightsClient({ relatedBlogPost, whitePaper }: Props) {
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const [selectedID, setSelectedID] = useState<WhitepaperInterface | null>(
    null,
  );
  const posts = useMemo(() => relatedBlogPost ?? [], [relatedBlogPost]);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const isMobile = useIsMobile()

  return (
    <>
      {mounted && (
        <section
          ref={sectionRef}
          className="tech-insights bg-[#000000] w-full relative overflow-hidden"
        >
          {/* Texture */}
          {!isMobile && <div
            className="texture-bg mix-blend-overlay w-full h-full absolute top-0 left-0 z-0"
            aria-hidden
          >
            <Image
              src={Images?.texture}
              alt=""
              fill
              sizes="(max-width: 768px) 100vw, (max-width: 1536px) 50vw, 700px"
              className="object-cover"
            />
          </div>}

          {/* Blur backgrounds */}
          <div className="w-[907px] h-[907px] bg-[#225bd4]/40 rounded-full blur-[333px] absolute right-0 top-0 translate-x-1/3 -translate-y-1/3" />
          <div className="w-[907px] h-[907px] bg-[#225bd4]/40 rounded-full blur-[333px] absolute left-0 bottom-0 -translate-x-1/3 translate-y-1/3" />

          <div className="max-w-[5000px] mx-auto w-[calc(100%_-_10%)] py-[40px] lg:py-[150px] relative z-2">
            {/* Header */}
            <div className="tech-insights-header flex flex-col lg:flex-row lg:items-center justify-between gap-[15px]">
              <div className="flex flex-col lg:flex-row lg:gap-[50px] lg:items-center w-full lg:w-auto">
                <p className="text-[#fff] text-base font-medium uppercase tracking-tight min-w-[17ch]">
                  Blogs & Insights
                </p>

                <h2 className="text-[length:var(--section-title)] font-medium clash-font leading-[110%]">
                  <span className="text-white">
                    Tech <span className="text-[#4a84ff]">Insights</span>
                  </span>
                </h2>
              </div>

              <PrimaryBtn
                text="View all"
                dark
                iconSrc={Icons?.chevronBlue}
                href="/blog/"
              />
            </div>

            {/* Blog Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-[30px] pt-[30px] lg:pt-[100px] min-h-[650px]">
              {posts.length === 0
                ? Array.from({ length: 2 })?.map((_, i) => (
                  <BlogSkeletonCard key={`skeleton-${i}`} />
                ))
                : posts?.map((item) => {
                  const blogUrl = item?.slug ? `/blog/${item.slug}` : "/blog";
                  return (
                    <article
                      key={item.id ?? item.slug}
                      className="p-[42px] group bg-white/10 flex flex-col justify-between items-start insight-card h-full transition-colors duration-300 hover:bg-white/20"
                    >
                      <div className="flex flex-col gap-[30px] max-w-[820px]">
                        <div className="flex flex-col gap-[15px]">
                          <div
                            className="text-white/50 text-base font-medium uppercase tracking-tight"
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(
                                item?.category?.name ?? "",
                              ),
                            }}
                          />

                          <h3
                            className="text-white text-[length:var(--sucess-card-title)] font-medium clash-font capitalize leading-[120%]"
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(item?.title ?? ""),
                            }}
                          />
                        </div>

                        <p className="text-white/50 text-[length:var(--hero-para)] leading-[150%]">
                          {item?.excerpt ??
                            "Learn how modern technologies are transforming digital platforms."}
                        </p>
                      </div>

                      <div className="inline-flex items-center gap-3 pt-5">
                        <Link
                          href={blogUrl + "/"}
                          aria-label={`Read more about ${item?.title || "this blog post"}`}
                          className="text-white text-[16px] sm:text-2xl font-medium"
                        >
                          Learn more
                        </Link>

                        <Link
                          href={blogUrl + "/"}
                          aria-label={`Read more about ${item?.title || "this blog post"}`}
                        >
                          <div className="arrow-button relative flex overflow-hidden rounded-full outline outline-white w-[36px] h-[36px] sm:w-12 sm:h-12 transition-all duration-300 group-hover:rotate-[-45deg] group-hover:bg-white">
                            <Image
                              src={Icons?.arrowLeft}
                              alt=""
                              width={15}
                              height={15}
                              sizes="(max-width: 1024px) 100vw, 50vw"
                              className="m-auto object-cover group-hover:invert"
                            />
                          </div>
                        </Link>
                      </div>
                    </article>
                  );
                })}

              {/* Whitepaper */}
              <article
                className="group relative flex flex-col justify-end insight-card cursor-pointer overflow-hidden w-full aspect-[3/4] lg:aspect-[9/14] 2xl:aspect-[9/12]"
                onClick={() => whitePaper && setSelectedID(whitePaper)}
              >
                <div className="absolute inset-0 w-full h-full bg-white/5">
                  {whitePaper?.thumb && (
                    <Image
                      src={whitePaper?.baseUrl && whitePaper?.thumb ? `${whitePaper.baseUrl}${whitePaper.thumb}` : ""}
                      alt={whitePaper.title || "Whitepaper"}
                      fill
                      sizes="(max-width:768px) 100vw, 33vw"
                      className="object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                    />
                  )}

                  <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                </div>

                {whitePaper?.title ? <div
                  className="relative z-10 p-[42px] flex items-center gap-3"
                  role="button"
                  aria-label={`Download PDF: ${whitePaper?.title || "Whitepaper"}`}
                >
                  <span className="text-white text-[16px] sm:text-2xl font-medium">
                    Download PDF
                  </span>

                  <div className="arrow-button relative flex overflow-hidden rounded-full outline outline-white w-[36px] h-[36px] sm:w-12 sm:h-12 transition-all duration-300 group-hover:rotate-[-45deg] group-hover:bg-white">
                    <Image
                      src={Icons?.arrowLeft}
                      alt=""
                      width={15}
                      height={15}
                      sizes="(max-width: 1024px) 100vw, 50vw"
                      className="m-auto object-cover group-hover:invert"
                    />
                  </div>
                </div> : null}

              </article>
            </div>
          </div>
        </section >
      )
      }

      {
        selectedID && (
          <WhitepaperDownloadPopup
            whitePaper={selectedID}
            onClose={() => setSelectedID(null)}
          />
        )
      }
    </>
  );
}
